import React, { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Caption1 } from '@ui/Typography'
import { Radio, RadioProps, RadioSize } from '@ui/components/Radio'
import type { SpacingType } from '@ui/design-system/spacing'

import { Box } from '@shared/components/Box'

interface RadioGroupContextValue<T = string | number> {
  name: string
  selectedValue?: T
  onChange: (value: T) => void
  size: RadioSize
  disabled?: boolean
}

const RadioGroupContext = createContext<RadioGroupContextValue<any> | null>(null)

export const useRadioGroupContext = () => useContext(RadioGroupContext)

interface RadioGroupProps<T = string | number> {
  name?: string
  label?: React.ReactNode
  description?: React.ReactNode

  value?: T
  defaultValue?: T
  onChange?: (value: T) => void
  size?: RadioSize
  disabled?: boolean
  horizontal?: boolean
  gap?: SpacingType
  error?: string
  children: React.ReactNode
}

export function RadioGroup<T = string | number>({
  name,
  label,
  description,
  value,
  defaultValue,
  onChange,
  size = 'lg',
  disabled,
  horizontal = false,
  gap = 'x12',
  children,
  error,
}: RadioGroupProps<T>) {
  const { t } = useTranslation()
  const autoName = React.useId()
  const groupName = name ?? `rg-${autoName}`

  const [uncontrolled, setUncontrolled] = React.useState<T | undefined>(defaultValue)
  const isControlled = value !== undefined
  const selectedValue = isControlled ? value : uncontrolled

  const idBase = React.useId()
  const descId = description ? `${idBase}-desc` : undefined
  const errId = error ? `${idBase}-err` : undefined
  const describedBy = [descId, errId].filter(Boolean).join(' ') || undefined

  const handleChange = (val: T) => {
    if (!isControlled) {
      setUncontrolled(val)
    }
    onChange?.(val)
  }

  const isRadioElement = (el: any): el is React.ReactElement<RadioProps<T>> =>
    !!el?.type && (el.type === Radio || el.type.displayName === 'Radio')

  const enhanceDirectRadioChild = (node: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(node)) return node

    if (isRadioElement(node)) {
      const cProps = node.props
      const childVal = cProps.value as T | undefined

      const nextChecked =
        childVal !== undefined && selectedValue !== undefined
          ? selectedValue === childVal
          : cProps.checked

      const nextOnChange = childVal !== undefined ? () => handleChange(childVal) : cProps.onChange

      return React.cloneElement(node, {
        name: groupName,
        error: !!error,
        size: size ?? cProps.size,
        disabled: disabled ?? cProps.disabled,
        checked: nextChecked,
        onChange: nextOnChange,
      })
    }

    return node
  }

  return (
    <RadioGroupContext.Provider
      value={{
        name: groupName,
        selectedValue,
        onChange: handleChange,
        size,
        disabled,
      }}
    >
      <fieldset name={name} aria-describedby={describedBy} disabled={disabled}>
        {label && <legend>{label}</legend>}

        <Box flex direction={horizontal ? 'row' : 'column'} gap={gap} fullWidth>
          {React.Children.map(children, enhanceDirectRadioChild)}
        </Box>

        {description && (
          <Box pl='x12' mt='x8'>
            <Caption1 id={descId} color='text-med-em'>
              {description}
            </Caption1>
          </Box>
        )}
        {error && (
          <Box pl='x12' mt='x8'>
            <Caption1 id={errId} color='text-danger-high-em'>
              {t(error)}
            </Caption1>
          </Box>
        )}
      </fieldset>
    </RadioGroupContext.Provider>
  )
}
